/* The lobby: which rooms exist, and which one to walk into. */

/*
  *** auto-fill WITH A MAX, AND A CAPPED TRACK ***
  An institute with one library would otherwise show a single card hugging
  the left edge of a 1600px screen. Capping the column keeps a lone card
  card-shaped rather than a banner, and auto-fill still lays several out in a row.
*/
.grid {
  display: grid;
  gap: var(--space-4);
  grid-template-columns: repeat(auto-fill, minmax(20rem, 24rem));
}

.card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-5);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
}

/* The room you are sitting in, so it is findable at a glance in a long list. */
.cardActive {
  border-color: var(--indigo);
  box-shadow: 0 0 0 1px var(--indigo);
}

.cardHead {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
}

.name {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text);
  line-height: 1.3;
}

.description {
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-muted);
  line-height: 1.5;
}

.meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-2);
  margin: 0;
}

.meta dt {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.meta dd {
  margin: 0.125rem 0 0;
  font-size: var(--text-sm);
  color: var(--text);
}

/* "No mentor right now" — an expectation, not a warning. Quiet on purpose. */
.note {
  margin: 0;
  font-size: 0.8rem;
  color: var(--text-muted);
  line-height: 1.45;
}

.actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-top: auto;
  padding-top: var(--space-1);
}

/*
  The reason beside a disabled button.

  *** NOT STYLED AS AN ERROR ***
  A library being full is not the student's mistake, and red would say it
  was. Muted text states the fact; the toast carries the full sentence when
  they actually try.
*/
.refusal {
  font-size: 0.8rem;
  color: var(--text-muted);
}

.muted {
  color: var(--text-muted);
  font-size: var(--text-sm);
}

.error {
  color: var(--danger);
  font-size: var(--text-sm);
}

/*
  Below the card's own minimum there is nothing to lay out side by side, so
  the track becomes a single full-width column rather than a 20rem card with
  a scrollbar beside it.
*/
@media (max-width: 640px) {
  .grid {
    grid-template-columns: 1fr;
  }

  .card {
    padding: var(--space-4);
  }
}

/* The lobby row: a block of detail, with Enter pinned to the bottom so a
   long description never pushes the button out of reach. */
.cardBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

/*
  *** A MENTOR IN THE ROOM IS THE REASON TO PICK IT ***
  Rendered as a marker rather than plain text: in a list of rooms, "a
  mentor is here to ask" is the single fact that decides which one a
  student walks into, and it was previously indistinguishable from the
  seat count beside it.
*/
.mentorHere {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  color: var(--indigo);
  font-weight: 600;
}

.mentorHere::before {
  content: '';
  width: 0.5rem;
  height: 0.5rem;
  border-radius: var(--radius-full);
  background: var(--success);
}

/*
  A mentor's hours on duty, above the rooms.

  Deliberately a quiet line rather than the big stat tile a student's study
  total gets: supervision time is a record, not a score. A mentor appears on
  no leaderboard, so presenting it like one would promise a ranking that
  does not exist.
*/
.dutyTotal {
  margin: 0;
  color: var(--text-muted);
  font-size: var(--text-sm);
}

.dutyTotal strong {
  color: var(--text);
  font-size: var(--text-base);
  font-variant-numeric: tabular-nums;
}
